import React, { useState, useEffect } from "react";
import { OwnerAndEmployeeCensusType, SingleCensusType } from "types";
import FooterButton from "uicomponents/FooterButton";
import {
  trackPageViewUserStep,
  trackBrokenKafkaPrefills,
} from "utils/analytics/trackEvent";
import CensusForm from "./components/CensusForm";
import styles from "./OwnerAndEmployeeCensus.module.css";
import {
  BACK_BUTTON_TEXT,
  NEXT_BUTTON_TEXT,
  LOADING_BUTTON_TEXT,
} from "pages/WorkersCompApplcation/utils/contants";
import { getMemberUuid } from "utils/idHelpers";

interface IOwnerEmployeeCensusProps {
  setNextStep: () => void;
  setPreviousStep: () => void;
  data: OwnerAndEmployeeCensusType;
}

const OwnerEmployeeCensus = ({
  ...props
}: IOwnerEmployeeCensusProps): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageViewUserStep(3, "owners");
    trackPageViewUserStep(4, "employee");
  }, []);

  const { setNextStep, setPreviousStep, data } = props;

  const savedCensus = data?.employeeCensuses?.filter(
    (emp) =>
      emp.isCompanyOwner === true ||
      (emp?.isCompanyEmployee === true && emp?.email)
  );

  data?.employeeCensuses?.forEach((emp) => {
    if (emp?.email === "-") {
      trackBrokenKafkaPrefills("Prospect blocked by kafka prefill error");
    }
  });

  const [censusList, setCensusList] = useState(savedCensus);
  const [isEditing, setIsEditing] = useState(censusList?.length ? false : true);
  const [addNewCensus, setAddNewCensus] = useState(
    censusList?.length ? false : true
  );
  const [isLoadingSteps, setIsLoadingSteps] = useState(false);

  const updateCensusList = (
    operation: string,
    newCensus?: SingleCensusType,
    uuid?: string
  ) => {
    if (operation === "Add" && newCensus) {
      addCensusList(newCensus);
    }
    if (operation === "Delete") {
      deleteCensusList(uuid);
    }
  };

  const addCensusList = (newCensus: SingleCensusType) => {
    if (!censusList) {
      return;
    }
    const hasCensusIndex = censusList?.findIndex(
      (census) => census?.uuid === newCensus?.uuid
    );

    // Update exisitng owner
    if (hasCensusIndex >= 0) {
      let newOwnerList = [...censusList];
      newOwnerList[hasCensusIndex] = newCensus;
      setCensusList(newOwnerList);
    } else {
      // Add new owner
      setCensusList([...censusList, newCensus]);
    }
    setAddNewCensus(false);
  };

  const deleteCensusList = (uuid?: string) => {
    // Delete Owner
    const newCensusList = censusList?.filter((census) => census?.uuid !== uuid);

    // Need atleast 1 owner
    if (newCensusList?.length === 0) {
      setAddNewCensus(true);
    } else {
      setAddNewCensus(false);
    }
    setCensusList(newCensusList);
  };

  const handleAddNewCensus = () => {
    setIsEditing(true);
    setAddNewCensus(true);
  };

  const updateIsEditing = (updateIsEditing: boolean) => {
    setIsEditing(updateIsEditing);
  };

  const handleBack = (): void => {
    setPreviousStep();
  };

  const handleNext = () => {
    setIsLoadingSteps(true);
    setNextStep();
  };

  const hasOneW2 = censusList?.some(
    (census) =>
      census.employeeInformation?.isPaid === "Paid" ||
      census.isCompanyEmployee === true
  );

  const hasAtLeast2Census = (censusList?.length ?? 0) >= 2;
  const hasMinReqForW2Emp = hasAtLeast2Census && hasOneW2;

  const isFooterButtonDisabled = isEditing || !hasMinReqForW2Emp;

  const sortedCensusList = [...(censusList || [])].sort((a, b) => {
    const memberUuid = getMemberUuid();
    return a.uuid === memberUuid ? -1 : b.uuid === memberUuid ? 1 : 0;
  });

  return (
    <>
      <div className={styles.title}>Company staff</div>
      <div className={styles.description}>
        <p>
          Tell us about the team you have right now.{" "}
          <strong>You'll need at least 2 team members</strong> who both reside
          and work in the U.S. and complete all the onboarding requirements.
        </p>
        <p>Please do not enter investor information.</p>
      </div>
      {sortedCensusList?.length ? (
        sortedCensusList.map((census, index) => {
          return (
            <CensusForm
              key={index}
              data={census}
              updateCensusList={updateCensusList}
              censusList={sortedCensusList}
              updateIsEditing={updateIsEditing}
              handleAddNewCensus={handleAddNewCensus}
              officeLocations={data?.officeLocations}
            />
          );
        })
      ) : (
        <></>
      )}
      {addNewCensus && (
        <CensusForm
          updateCensusList={updateCensusList}
          updateIsEditing={updateIsEditing}
          handleAddNewCensus={handleAddNewCensus}
          officeLocations={data?.officeLocations}
        />
      )}
      <FooterButton
        nextButtonText={isLoadingSteps ? LOADING_BUTTON_TEXT : NEXT_BUTTON_TEXT}
        backButtonText={BACK_BUTTON_TEXT}
        buttonType="button"
        handleBackClick={handleBack}
        handleNextClick={handleNext}
        disabled={isFooterButtonDisabled || isLoadingSteps}
      />
    </>
  );
};

export default OwnerEmployeeCensus;
